import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { Link as RouterLink } from "react-router-dom";
import api from "../../api";
import { toast } from "react-toastify";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import AnalyticsFiltersBar from "./AnalyticsFiltersBar";
import useAnalyticsFilters from "./useAnalyticsFilters";
import AnalyticsDataTable, { StatusChip } from "./AnalyticsDataTable";

const TILE_CONFIGS = [
  { key: "students_overdue",   label: "Students Overdue",    Icon: PersonIcon,          iconBg: "#fee2e2", iconColor: "#dc2626" },
  { key: "outstanding_amount", label: "Outstanding Amount",  Icon: CurrencyRupeeIcon,   iconBg: "#dcfce7", iconColor: "#16a34a", prefix: "£" },
  { key: "due_this_week",      label: "Due This Week",       Icon: CalendarMonthIcon,   iconBg: "#fff7ed", iconColor: "#ea580c" },
];

function KpiTile({ label, value, Icon, iconBg, iconColor, prefix }) {
  const display = prefix
    ? `${prefix}${Number(value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
    : (value ?? 0).toLocaleString();
  return (
    <Paper sx={{ ...CARD_SX, display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ color: iconColor, fontSize: 26 }} />
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{display}</Typography>
      </Box>
    </Paper>
  );
}

export default function PaymentOverdueAnalytics() {
  const [courseId, setCourseId] = useState("");
  const [academicCourseId, setAcademicCourseId] = useState("");
  const [planId, setPlanId] = useState("");
  const [dueStatus, setDueStatus] = useState("overdue");
  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  const { courses, academicCourses, plans } = useAnalyticsFilters({ courseId, academicCourseId });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { due_status: dueStatus };
      if (courseId) params.course_id = courseId;
      if (academicCourseId) params.academic_course_id = academicCourseId;
      if (planId) params.course_plan_id = planId;
      const res = await api.get("admin/analytics/payment-overdue", { params });
      const d = res.data?.data || {};
      setSummary(d.summary || {});
      setRows(d.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load payments");
      setSummary({});
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [courseId, academicCourseId, planId, dueStatus]);

  useEffect(() => { load(); }, [load]);

  const sendReminder = async (row) => {
    try {
      setSendingId(row.installment_id);
      await api.post("admin/analytics/payment-overdue/remind", { installment_id: row.installment_id });
      toast.success("Reminder sent");
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to send reminder");
    } finally {
      setSendingId(null);
    }
  };

  const columns = [
    { key: "student_name", label: "Student" },
    { key: "batch_name", label: "Group" },
    { key: "parent_email", label: "Parent Contact", render: (r) => r.parent_email || r.parent_phone || "—" },
    { key: "due_date", label: "Due Date" },
    { key: "amount_due", label: "Amount Due", render: (r) => `£${Number(r.amount_due || 0).toFixed(2)}` },
    {
      key: "overdue_days",
      label: "Days Overdue",
      render: (r) => (
        <Typography
          variant="body2"
          sx={{ color: r.overdue_days > 7 ? "#dc2626" : r.overdue_days > 3 ? "#d97706" : "inherit", fontWeight: r.overdue_days > 3 ? 700 : 400 }}
        >
          {r.overdue_days ?? 0}
        </Typography>
      ),
    },
    { key: "status", label: "Status", render: (r) => <StatusChip status={r.status} /> },
  ];

  return (
    <AnalyticsPageShell
      title="Instalment Deadline Monitoring"
      subtitle="Track students who have missed instalment deadlines and take timely action."
      filters={
        <AnalyticsFiltersBar
          courses={courses}
          academicCourses={academicCourses}
          plans={plans}
          courseId={courseId}
          setCourseId={setCourseId}
          showAcademicCourse={false}
          academicCourseId={academicCourseId}
          setAcademicCourseId={setAcademicCourseId}
          planId={planId}
          setPlanId={setPlanId}
          showDueStatus
          dueStatus={dueStatus}
          setDueStatus={setDueStatus}
        />
      }
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {TILE_CONFIGS.map(({ key, label, Icon, iconBg, iconColor, prefix }) => (
          <Grid item xs={12} sm={4} key={key}>
            <KpiTile label={label} value={summary[key]} Icon={Icon} iconBg={iconBg} iconColor={iconColor} prefix={prefix} />
          </Grid>
        ))}
      </Grid>
      <AnalyticsDataTable
        columns={columns}
        rows={rows}
        loading={loading}
        renderActions={(row) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<SendIcon />}
              disabled={sendingId === row.installment_id || !row.parent_email}
              onClick={() => sendReminder(row)}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              Send Reminder
            </Button>
            {row.student_id && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityIcon />}
                component={RouterLink}
                to={`/admin/student/${row.student_id}`}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                View Account
              </Button>
            )}
          </Box>
        )}
      />
    </AnalyticsPageShell>
  );
}
